import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { LogOut, Home, User, Play, X, FileText, Loader2, Send, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeader } from "@/context/AuthContext";
import { SEGMENTS, SANTRI_HIDDEN_KEYS } from "@/lib/santriSchema";
import { todayISO, formatIsoDate, buildWaMessage, buildWaLink } from "@/lib/perizinan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SantriDashboard() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(user?.profile || null);
  const [loading, setLoading] = useState(!user?.profile);
  const [perizinanList, setPerizinanList] = useState([]);
  const [waNumber, setWaNumber] = useState("");
  const [permitModal, setPermitModal] = useState(false);
  const navigate = useNavigate();

  const loadPerizinan = async () => {
    try {
      const res = await axios.get(`${API}/perizinan/mine`, { headers: authHeader() });
      setPerizinanList(res.data || []);
    } catch {}
  };

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/me`, { headers: authHeader() })
      .then((res) => { if (!cancelled) { setProfile(res.data.profile || null); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    axios.get(`${API}/settings/public`)
      .then((r) => setWaNumber(r.data?.admin_wa_number || ""))
      .catch(() => {});
    loadPerizinan();
    return () => { cancelled = true; };
  }, []);

  const doLogout = () => { logout(); navigate("/", { replace: true }); };

  const formatTanggal = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
    } catch { return d; }
  };

  const formatValue = (field, val) => {
    if (val === undefined || val === null || String(val).trim() === "") return "—";
    if (field.type === "date") return formatTanggal(val);
    return val;
  };

  return (
    <main className="min-h-screen bg-paper text-forest">
      <DashboardNav user={user} onLogout={doLogout} />

      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="text-xs tracking-[0.4em] uppercase text-gold">Portal Santri</span>
          <h1 className="mt-3 font-serif-display text-forest text-4xl md:text-5xl leading-tight">
            Ahlan wa sahlan,<br />
            <span className="italic text-gold">
              {profile?.nama_santri || user?.name || "Santri"}.
            </span>
          </h1>
          <p className="mt-4 font-arabic text-2xl md:text-3xl text-gold">بَارَكَ اللَّهُ فِيكَ</p>
        </motion.div>

        {loading ? (
          <p className="mt-12 text-moss">Memuat data…</p>
        ) : (
          <>
            {/* Header identity */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-12 p-6 md:p-8 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
              data-testid="santri-identity"
            >
              <div className="flex items-center gap-6 flex-shrink-0">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-forest text-parchment overflow-hidden border-2 border-gold/40 flex items-center justify-center flex-shrink-0">
                  {profile?.foto_profil ? (
                    <img src={profile.foto_profil} alt="Foto profil" className="w-full h-full object-cover" />
                  ) : (
                    <User size={36} />
                  )}
                </div>
                <div>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Username</span>
                  <p className="font-serif-display text-2xl md:text-3xl mt-1">{user?.username}</p>
                  <p className="mt-1 text-sm text-moss">
                    {profile?.status_santri || "Santri"}
                    {profile?.tahun_masuk_grq ? ` • Angkatan ${profile.tahun_masuk_grq}` : ""}
                  </p>
                  <button
                    onClick={() => setPermitModal(true)}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors"
                    data-testid="santri-ajukan-izin"
                  >
                    <FileText size={14} /> Ajukan Perizinan
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px self-stretch bg-gold/30" />

              {/* Jumlah Hafalan Terakhir — hero-sized display */}
              <div className="flex-1 md:text-right" data-testid="santri-hafalan-headline">
                <span className="text-[10px] tracking-[0.3em] uppercase text-gold">
                  Jumlah Hafalan Terakhir
                </span>
                <p className="mt-2 font-serif-display text-forest text-4xl md:text-5xl leading-[1.05]">
                  {profile?.jumlah_hafalan_terakhir && String(profile.jumlah_hafalan_terakhir).trim()
                    ? (
                        <span className="italic text-gold">{profile.jumlah_hafalan_terakhir}</span>
                      )
                    : (
                        <span className="text-moss text-3xl md:text-4xl not-italic">Belum ada data</span>
                      )}
                </p>
              </div>
            </motion.div>

            {/* Segments */}
            {SEGMENTS.filter((s) => s.id !== "media").map((seg, si) => {
              const visibleFields = seg.fields.filter(
                (f) => !SANTRI_HIDDEN_KEYS.includes(f.key) && f.key !== "username" && f.key !== "password"
              );
              if (visibleFields.length === 0) return null;
              return (
                <motion.section
                  key={seg.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.05 + si * 0.05 }}
                  className="mt-8 p-6 md:p-8 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur"
                  data-testid={`santri-segment-${seg.id}`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-1 h-6 bg-gold rounded-full" />
                    <h2 className="font-serif-display text-forest text-2xl md:text-3xl">{seg.label}</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {visibleFields.map((f) => (
                      <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-moss">{f.label}</p>
                        <p className={`mt-1 text-forest ${f.type === "textarea" ? "leading-relaxed" : ""} text-sm md:text-base whitespace-pre-wrap`}>
                          {formatValue(f, profile?.[f.key])}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.section>
              );
            })}

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-10 p-6 md:p-8 rounded-2xl border border-gold/30 bg-forest text-parchment"
            >
              <p className="font-arabic text-2xl md:text-3xl text-gold leading-loose">
                وَلَقَدْ يَسَّرْنَا الْقُرْآنَ لِلذِّكْرِ
              </p>
              <p className="mt-4 text-parchment/80 italic">
                &ldquo;Dan sungguh telah Kami mudahkan Al-Qur&apos;an untuk pelajaran.&rdquo; (QS. Al-Qamar: 17)
              </p>
              <p className="mt-6 text-xs text-parchment/60">
                Data profil di atas dikelola oleh Admin Pondok. Hubungi admin untuk perubahan data.
              </p>
            </motion.div>
            <motion.section
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-8 p-6 md:p-8 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur"
              data-testid="santri-segment-perizinan"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="w-1 h-6 bg-gold rounded-full" />
                <h2 className="font-serif-display text-forest text-2xl md:text-3xl">Perizinan</h2>
              </div>
              {perizinanList.length === 0 ? (
                <p className="text-sm text-moss">Belum ada pengajuan perizinan.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-forest text-parchment text-left">
                      <tr>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">No</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Tgl Pengajuan</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Alasan</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Rencana Kembali</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Approver</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Tgl Approve</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perizinanList.map((p, i) => (
                        <tr key={p.id} className="border-t border-gold/15">
                          <td className="px-3 py-2 text-moss">{i + 1}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatIsoDate(p.tanggal_pengajuan)}</td>
                          <td className="px-3 py-2 max-w-[320px]">{p.alasan || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatIsoDate(p.tanggal_rencana_kembali)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{p.approver || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{p.tanggal_approve ? formatIsoDate(p.tanggal_approve) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.section>

          </>
        )}
      </section>

      <AnimatePresence>
        {permitModal && (
          <PermitModal
            profile={profile}
            username={user?.username}
            waNumber={waNumber}
            onClose={() => setPermitModal(false)}
            onSubmitted={loadPerizinan}
          />
        )}
      </AnimatePresence>

      {profile?.link_youtube ? (
        <FloatingYoutube link={profile.link_youtube} />
      ) : null}
    </main>
  );
}

function PermitModal({ profile, username, waNumber, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    tanggal_pengajuan: todayISO(),
    nama_lengkap: profile?.nama_santri || username || "",
    alasan: "",
    tanggal_rencana_kembali: "",
  });
  const [busy, setBusy] = useState(false);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!form.alasan.trim() || !form.tanggal_rencana_kembali) {
      toast.error("Alasan dan Tanggal Rencana Kembali wajib diisi");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/perizinan`, form, { headers: authHeader() });
      toast.success("Perizinan terkirim. Membuka WhatsApp…");
      onSubmitted?.();
      const message = buildWaMessage(form);
      const link = buildWaLink(waNumber, message);
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
      } else {
        toast.warning("Nomor WhatsApp Admin belum diset. Hubungi Admin.");
      }
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gagal mengirim perizinan";
      toast.error(typeof msg === "string" ? msg : "Gagal mengirim");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}
      data-testid="permit-modal">
      <motion.div initial={{ y: 30, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-parchment rounded-2xl w-full max-w-lg overflow-hidden border border-gold/40 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Form Perizinan</span>
            <h3 className="font-serif-display text-forest text-2xl">Ajukan Izin</h3>
          </div>
          <button onClick={onClose} className="text-forest hover:text-gold p-2" aria-label="Tutup"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="px-6 py-6 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-1.5">Tanggal Pengajuan</label>
            <input type="date" value={form.tanggal_pengajuan} onChange={(e) => setF("tanggal_pengajuan", e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
              data-testid="permit-tanggal_pengajuan" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-1.5">Nama Lengkap</label>
            <input type="text" value={form.nama_lengkap} onChange={(e) => setF("nama_lengkap", e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
              data-testid="permit-nama_lengkap" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-1.5">Alasan Izin</label>
            <textarea rows={3} value={form.alasan} onChange={(e) => setF("alasan", e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
              data-testid="permit-alasan" required />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-1.5">Tanggal Rencana Kembali</label>
            <input type="date" value={form.tanggal_rencana_kembali} onChange={(e) => setF("tanggal_rencana_kembali", e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
              data-testid="permit-tanggal_rencana_kembali" required />
          </div>

          <p className="text-[11px] text-moss">
            Setelah submit, data tersimpan & Anda akan diarahkan ke WhatsApp Admin secara otomatis.
          </p>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-forest text-sm hover:bg-sand transition-colors" data-testid="permit-cancel">Batal</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors disabled:opacity-60" data-testid="permit-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {busy ? "Mengirim…" : "Submit & Kirim WhatsApp"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function getYoutubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const m = u.pathname.match(/\/embed\/([^/?#]+)/);
    if (m) return m[1];
  } catch {}
  return null;
}

function FloatingYoutube({ link }) {
  const [closed, setClosed] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    const onScroll = () => setMinimized(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (closed) return null;
  const id = getYoutubeId(link);
  const thumb = id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;

  const openLink = () => window.open(link, "_blank", "noopener,noreferrer");

  return (
    <div className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40" data-testid="santri-floating-youtube">
      {minimized || !thumb ? (
        <button
          type="button"
          onClick={openLink}
          aria-label="Buka video profil"
          className="relative group"
          data-testid="floating-yt-mini"
        >
          <span className="absolute inset-0 rounded-full bg-red-600/50 animate-ping" />
          <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-600 text-white shadow-xl ring-4 ring-red-600/20 hover:bg-red-700 transition-colors">
            <Play size={22} className="ml-0.5 fill-current" />
          </span>
          <span className="hidden md:inline-flex absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-full bg-forest text-parchment text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            Profil Video Anda
          </span>
        </button>
      ) : (
        <div className="relative group" data-testid="floating-yt-card">
          <button
            type="button"
            aria-label="Tutup"
            onClick={() => setClosed(true)}
            className="absolute -top-2 -right-2 z-10 w-7 h-7 rounded-full bg-forest text-parchment shadow-md flex items-center justify-center hover:bg-gold hover:text-forest transition-colors"
            data-testid="floating-yt-close"
          >
            <X size={14} />
          </button>
          <button
            type="button"
            onClick={openLink}
            className="block w-[220px] md:w-[260px] rounded-2xl overflow-hidden border border-gold/40 shadow-2xl bg-forest text-left"
            style={{ boxShadow: "0 24px 48px -16px rgba(10,46,34,0.4)" }}
            data-testid="floating-yt-open"
          >
            <div className="relative aspect-video overflow-hidden">
              <img src={thumb} alt="Preview YouTube" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/20 to-transparent" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/95 text-forest shadow-xl">
                  <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
                  <Play size={22} className="ml-1 fill-current" />
                </span>
              </span>
              <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600 text-white text-[10px] tracking-widest uppercase font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                YouTube
              </span>
            </div>
            <div className="px-3 py-3 bg-forest text-parchment">
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold">Profil Video</p>
              <p className="mt-1 text-xs md:text-sm leading-snug line-clamp-2">
                Klik untuk membuka video profil Anda
              </p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

function DashboardNav({ user, onLogout }) {
  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-parchment/85 backdrop-blur-md border-b border-gold/30">
      <nav className="max-w-5xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="dash-brand">
          <img src="https://customer-assets.emergentagent.com/job_generasi-flow/artifacts/fgw1jyfc_LOGO%20PONDOK%20GRQ%202022_alter.png" alt="Logo GRQ" className="h-11 w-11 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-moss">Portal Santri</div>
            <div className="font-serif-display text-forest text-base">Generasi Rabbani Qurani</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline text-xs text-moss">
            <span className="tracking-[0.2em] uppercase">Ahlan</span>{" "}
            <span className="text-forest font-medium">{user?.name}</span>
          </span>
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-forest text-xs hover:bg-gold hover:text-forest transition-colors" data-testid="dash-home">
            <Home size={14} /> Beranda
          </Link>
          <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors" data-testid="dash-logout">
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </nav>
    </header>
  );
}
